import { useMutation, useQuery } from "@apollo/client";
import { compact } from "lodash";
import moment from "moment";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  AssessmentGroupProviders,
  AssessmentType,
  ComplianceProgramRecurrence,
  ComplianceProgramScheduledReminderFragmentFragment,
  ComplianceProgramState,
  CreateProgramScheduledReminderMutationMutation,
  CreateProgramScheduledReminderMutationMutationVariables,
  DeleteComplianceProgramScheduledReminderMutationMutation,
  DeleteComplianceProgramScheduledReminderMutationMutationVariables,
  GetComplianceProgramQuery,
  GetComplianceProgramScheduledRemindersQuery,
  GetComplianceProgramScheduledRemindersQueryVariables,
} from "types/graphql-schema";

import useLabel from "@apps/use-label/use-label";
import { successNotificationVar } from "@cache/cache";
import Button from "@components/button/button";
import { buttonTheme } from "@components/button/button-constants";
import Loading from "@components/loading/loading";
import Table, {
  TableBody,
  TableBodyCell,
  TableBodyRow,
  TableContainer,
  TableHeadCell,
  TableHeadRow,
} from "@components/table/table";
import Tooltip from "@components/tooltip/tooltip";
import { getTopicflowIcon } from "@components/topicflow-icon/topicflow-icon-helpers";
import { getAssessmentTypeLabel } from "@helpers/constants";
import { assertEdgesNonNull } from "@helpers/helpers";
import useConfirm from "@helpers/hooks/use-confirm";
import { pluralize, titleCase } from "@helpers/string";

import createProgramReminderMutation from "../graphql/create-program-reminder-mutation";
import deleteComplianceProgramScheduledReminderMutation from "../graphql/delete-compliance-program-scheduled-reminder-mutation";
import getComplianceProgramScheduledRemindersQuery from "../graphql/get-compliance-program-reminders-query";
import { useProgramsSection } from "../programs-section-context";
import { ComplianceProgramReportingEntityTableCol } from "./compliance-program-progress-entity-table";
import ComplianceProgramReminderDialog from "./compliance-program-reminder-dialog";

const ComplianceProgramReminderRow = ({
  reminder,
  isSendingNow,
  showRelativeDate,
  complianceProgramStartDate,
}: {
  showRelativeDate?: boolean;
  isSendingNow: boolean;
  complianceProgramStartDate?: string | null;
  reminder: ComplianceProgramScheduledReminderFragmentFragment;
}) => {
  const label = useLabel();
  const [deleteReminder, { loading }] = useMutation<
    DeleteComplianceProgramScheduledReminderMutationMutation,
    DeleteComplianceProgramScheduledReminderMutationMutationVariables
  >(deleteComplianceProgramScheduledReminderMutation);

  const { confirm, ConfirmationDialog } = useConfirm(
    "Delete reminder?",
    "Are you sure you want to delete this reminder?",
  );

  const handleDeleteReminder = async () => {
    const confirmed = await confirm();
    if (confirmed) {
      deleteReminder({
        variables: { reminderId: reminder.id },
      });
    }
  };

  return (
    <TableBodyRow key={reminder.id}>
      {isSendingNow ? (
        <TableBodyCell className="text-gray-800 w-[220px]">
          Sending now...
        </TableBodyCell>
      ) : reminder.sentDatetime ? (
        <TableBodyCell className="text-gray-800 w-[220px]">
          {moment(reminder.sentDatetime).format("LLLL")}
        </TableBodyCell>
      ) : (
        <TableBodyCell className="text-gray-800 w-[220px]">
          {complianceProgramStartDate && !showRelativeDate
            ? moment(complianceProgramStartDate)
                .add(reminder.daysAfterStartDate, "days")
                .format("MMM D, YYYY")
            : `${reminder.daysAfterStartDate} ${titleCase(
                pluralize("day", reminder.daysAfterStartDate),
              )}`}
        </TableBodyCell>
      )}
      <TableBodyCell className="w-[192px]">
        {compact([
          reminder.nominations && "Peer Nominations",
          reminder.oneonone && label("oneonone", { titleCase: true }),
          reminder.performanceAssessment &&
            `${getAssessmentTypeLabel(AssessmentType.Performance)} ${label(
              "review",
              { titleCase: true },
            )}`,
          reminder.performanceSelfAssessment &&
            `${getAssessmentTypeLabel(
              AssessmentType.Performance,
              true,
            )} ${label("review", { titleCase: true })}`,
          reminder.managerAssessment &&
            getAssessmentTypeLabel(AssessmentType.Manager),
          reminder.peerAssessment &&
            getAssessmentTypeLabel(AssessmentType.Peer),
          reminder.engagementSurvey &&
            getAssessmentTypeLabel(AssessmentType.EngagementSurvey),
        ]).join(", ")}
      </TableBodyCell>
      <TableBodyCell className="w-[192px]">
        {compact([
          reminder.emailNotification && "Email",
          reminder.slackNotification && "Slack",
          reminder.teamsNotification && "Teams",
        ]).join(", ")}
      </TableBodyCell>
      <TableBodyCell>
        <Tooltip text={reminder.message}>
          <span className="truncate inline-block max-w-48">
            {reminder.message || "N/A"}
          </span>
        </Tooltip>
      </TableBodyCell>
      <TableBodyCell>
        {reminder.remindedUsersCount}{" "}
        {pluralize("employee", reminder.remindedUsersCount)}
      </TableBodyCell>
      <TableBodyCell className="w-[24px] flex justify-end">
        <ConfirmationDialog />
        {loading ? (
          <Loading mini size="5" />
        ) : !reminder.sentDatetime ? (
          <Button
            icon={getTopicflowIcon("trash")}
            theme={buttonTheme.iconGray}
            onClick={handleDeleteReminder}
          />
        ) : null}
      </TableBodyCell>
    </TableBodyRow>
  );
};

const ComplianceProgramReminders = ({
  complianceProgramId,
  complianceProgram,
}: {
  complianceProgram: NonNullable<
    GetComplianceProgramQuery["complianceProgram"]
  >;
  complianceProgramId: number;
}) => {
  const label = useLabel();
  const { noun } = useProgramsSection();
  const nounLower = noun.toLowerCase();
  const [isShowingSendReminderDialog, setIsShowingSendReminderDialog] =
    useState(false);
  const [sendingNowReminderId, setSendingNowReminderId] = useState<
    number | null
  >(null);

  const { data, loading } = useQuery<
    GetComplianceProgramScheduledRemindersQuery,
    GetComplianceProgramScheduledRemindersQueryVariables
  >(getComplianceProgramScheduledRemindersQuery, {
    variables: { complianceProgramId },
  });
  const reminders = useMemo(
    () =>
      data?.complianceProgram?.scheduledReminders
        ? assertEdgesNonNull(data.complianceProgram.scheduledReminders)
        : [],
    [data],
  );

  const sendingNowReminderSentDatetime = useMemo(() => {
    return reminders.find((reminder) => reminder.id === sendingNowReminderId)
      ?.sentDatetime;
  }, [reminders, sendingNowReminderId]);

  useEffect(() => {
    if (sendingNowReminderSentDatetime) {
      setSendingNowReminderId(null);
    }
  }, [sendingNowReminderSentDatetime]);

  const [createProgramReminder, { loading: createProgramReminderLoading }] =
    useMutation<
      CreateProgramScheduledReminderMutationMutation,
      CreateProgramScheduledReminderMutationMutationVariables
    >(createProgramReminderMutation);

  const handleClickCreateReminder = useCallback(
    (
      variables: Omit<
        CreateProgramScheduledReminderMutationMutationVariables,
        "complianceProgramId"
      >,
    ) => {
      createProgramReminder({
        variables: {
          ...variables,
          complianceProgramId,
        },
        refetchQueries: [getComplianceProgramScheduledRemindersQuery],
        onCompleted: (response) => {
          setIsShowingSendReminderDialog(false);
          successNotificationVar({ title: "Reminder created" });
          if (
            variables.sendNow &&
            response.createComplianceProgramScheduledReminder?.reminder
          ) {
            setSendingNowReminderId(
              response.createComplianceProgramScheduledReminder.reminder.id,
            );
          }
        },
      });
    },
    [createProgramReminder, complianceProgramId],
  );

  const scheduledReminders = useMemo(() => {
    return reminders.filter(
      (reminder) =>
        !reminder.sentDatetime && reminder.id !== sendingNowReminderId,
    );
  }, [reminders, sendingNowReminderId]);

  const sentReminders = useMemo(() => {
    return reminders.filter(
      (reminder) =>
        !!reminder.sentDatetime || reminder.id === sendingNowReminderId,
    );
  }, [reminders, sendingNowReminderId]);

  const performanceAssessmentTemplate = useMemo(
    () =>
      complianceProgram?.performanceAssessmentTemplate
        ? complianceProgram.performanceAssessmentTemplate
        : null,
    [complianceProgram],
  );
  const managerAssessmentTemplate = useMemo(
    () =>
      complianceProgram?.managerAssessmentTemplate
        ? complianceProgram.managerAssessmentTemplate
        : null,
    [complianceProgram],
  );
  const peerAssessmentTemplate = useMemo(
    () =>
      complianceProgram?.peerAssessmentTemplate
        ? complianceProgram.peerAssessmentTemplate
        : null,
    [complianceProgram],
  );
  const engagementSurveyTemplate = useMemo(
    () =>
      complianceProgram?.engagementSurveyTemplate
        ? complianceProgram.engagementSurveyTemplate
        : null,
    [complianceProgram],
  );
  const topicTemplate = useMemo(
    () =>
      complianceProgram?.requiredTopicTemplates
        ? assertEdgesNonNull(complianceProgram.requiredTopicTemplates)[0]
        : null,
    [complianceProgram],
  );

  const programRecurrence = complianceProgram.recurrence;
  const programOngoing = complianceProgram.ongoing;
  const showRelativeDate =
    programRecurrence !== ComplianceProgramRecurrence.None || programOngoing;

  const cols: ComplianceProgramReportingEntityTableCol[] = useMemo(() => {
    return compact([
      peerAssessmentTemplate &&
        peerAssessmentTemplate.providers !==
          AssessmentGroupProviders.Default && {
          label: `${getAssessmentTypeLabel(AssessmentType.Peer)} Nominations`,
          keyName: "nominations",
          remindUsers: undefined,
          remindUserType:
            peerAssessmentTemplate.providers ===
            AssessmentGroupProviders.ManagerSelect
              ? "manager"
              : "subject",
          remindUserTooltip:
            peerAssessmentTemplate.providers ===
            AssessmentGroupProviders.ManagerSelect
              ? `Managers of the peer ${label(
                  "review",
                )} subjects will be reminded to nominate peers.`
              : peerAssessmentTemplate.providers ===
                  AssessmentGroupProviders.SubjectSelect
                ? `Subjects will be reminded to nominate peers for their peer ${label(
                    "review",
                  )}.`
                : "",
        },
      performanceAssessmentTemplate &&
        performanceAssessmentTemplate.hasSelfAssessment && {
          label: `Self ${label("review", {
            capitalize: true,
          })}`,
          keyName: "performanceSelfAssessmentStatus",
          remindUserType: "subject",
          remindUsers: undefined,
        },
      performanceAssessmentTemplate && {
        label: `Manager ${label("review", { capitalize: true })}`,
        keyName: "performanceAssessmentStatus",
        remindUserType: "manager",
        remindUsers: undefined,
      },
      engagementSurveyTemplate && {
        label: `Survey`,
        keyName: "engagementSurveyStatus",
        remindUserType: "survey_participant",
        remindUsers: undefined,
      },
      peerAssessmentTemplate && {
        label: `${getAssessmentTypeLabel(AssessmentType.Peer)} ${label(
          "review",
          {
            capitalize: true,
          },
        )}`,
        keyName: "peerAssessmentStatus",
        remindUserType: "peer",
        remindUsers: undefined,
      },
      managerAssessmentTemplate && {
        label: `${getAssessmentTypeLabel(AssessmentType.Manager)} ${label(
          "review",
          { capitalize: true },
        )}`,
        keyName: "managerAssessmentStatus",
        remindUserType: "directReport",
        remindUsers: undefined,
      },
      topicTemplate && {
        label: "1-on-1 Meetings",
        keyName: "oneononeStatus",
        remindUserType: "manager",
        remindUsers: undefined,
      },
    ]);
  }, [
    peerAssessmentTemplate,
    engagementSurveyTemplate,
    performanceAssessmentTemplate,
    managerAssessmentTemplate,
    topicTemplate,
    label,
  ]);

  const createReminderButtonTooltip = useMemo(() => {
    if (complianceProgram.state !== ComplianceProgramState.Published) {
      return `The ${nounLower} needs to be published`;
    }
    if (!complianceProgram.canUpdate?.permission) {
      return `You do not have the permission to create a reminder. Only admins and creator of the ${nounLower} can perform this action.`;
    }
    return undefined;
  }, [complianceProgram, nounLower]);

  return (
    <div
      className="flex flex-col gap-10"
      aria-label="Compliance Program reminders"
    >
      {isShowingSendReminderDialog && complianceProgram.startDate && (
        <ComplianceProgramReminderDialog
          complianceProgramStartDate={moment(
            complianceProgram.startDate,
          ).toDate()}
          complianceProgramRecurrence={complianceProgram.recurrence}
          complianceProgramOngoing={complianceProgram.ongoing}
          onClose={() => setIsShowingSendReminderDialog(false)}
          cols={cols}
          selectedTeam={null}
          onSave={handleClickCreateReminder}
          createProgramReminderLoading={createProgramReminderLoading}
        />
      )}
      <div className="">
        <div className="flex justify-between items-center">
          <div className="tf-header-base-dark">Scheduled Reminders</div>
          <Button
            onClick={() => setIsShowingSendReminderDialog(true)}
            text="Create reminder"
            disabled={!!createReminderButtonTooltip}
            tooltip={createReminderButtonTooltip}
          />
        </div>
        <TableContainer className="mt-4">
          <Table>
            <TableHeadRow>
              <TableHeadCell roundedLeft widthPx={288}>
                {showRelativeDate ? "Days after start date" : "Scheduled Date"}
              </TableHeadCell>
              <TableHeadCell widthPx={192}>
                {label("review", { titleCase: true })} Steps
              </TableHeadCell>
              <TableHeadCell widthPx={192}>Reminder Type</TableHeadCell>
              <TableHeadCell>Message</TableHeadCell>
              <TableHeadCell widthPx={170}>Sent to</TableHeadCell>
              <TableHeadCell widthPx={24}> </TableHeadCell>
            </TableHeadRow>
            <TableBody>
              {scheduledReminders.map((reminder) => (
                <ComplianceProgramReminderRow
                  key={reminder.id}
                  reminder={reminder}
                  isSendingNow={reminder.id === sendingNowReminderId}
                  showRelativeDate={showRelativeDate}
                  complianceProgramStartDate={complianceProgram.startDate}
                />
              ))}
              {scheduledReminders.length === 0 && !loading && (
                <TableBodyRow>
                  <TableBodyCell
                    colSpan={6}
                    className="text-center p-4 text-gray-500"
                  >
                    No scheduled reminders.
                  </TableBodyCell>
                </TableBodyRow>
              )}
              {scheduledReminders.length === 0 && loading && (
                <TableBodyRow>
                  <TableBodyCell
                    colSpan={6}
                    className="text-center p-4 text-gray-500"
                  >
                    <Loading size="5" />
                  </TableBodyCell>
                </TableBodyRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </div>

      {sentReminders.length > 0 && (
        <div className="">
          <div className="flex justify-between items-center">
            <div className="tf-header-base-dark">Sent Reminders</div>
          </div>
          <TableContainer className="mt-4">
            <Table>
              <TableHeadRow>
                <TableHeadCell widthPx={288} roundedLeft>
                  Sent on
                </TableHeadCell>
                <TableHeadCell widthPx={192}>
                  {label("review", { titleCase: true })} Steps
                </TableHeadCell>
                <TableHeadCell widthPx={192}>Reminder Type</TableHeadCell>
                <TableHeadCell>Message</TableHeadCell>
                <TableHeadCell widthPx={170}>Sent to</TableHeadCell>
                <TableHeadCell widthPx={24}> </TableHeadCell>
              </TableHeadRow>
              <TableBody>
                {sentReminders.map((reminder) => (
                  <ComplianceProgramReminderRow
                    key={reminder.id}
                    reminder={reminder}
                    isSendingNow={reminder.id === sendingNowReminderId}
                    complianceProgramStartDate={complianceProgram.startDate}
                  />
                ))}
                {sentReminders.length === 0 && !loading && (
                  <TableBodyRow>
                    <TableBodyCell
                      colSpan={6}
                      className="text-center p-4 text-gray-500"
                    >
                      No reminders sent.
                    </TableBodyCell>
                  </TableBodyRow>
                )}
                {sentReminders.length === 0 && loading && (
                  <TableBodyRow>
                    <TableBodyCell
                      colSpan={6}
                      className="text-center p-4 text-gray-500"
                    >
                      <Loading size="5" />
                    </TableBodyCell>
                  </TableBodyRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </div>
      )}
    </div>
  );
};

export default ComplianceProgramReminders;
